import React, { useEffect, useContext } from 'react';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import { connect } from 'react-redux';
import { Acl } from 'modules/common/components/Acl';
import { getJson } from 'session';
import PropTypes from 'prop-types';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { ModalContext } from 'App';
import TransactionInformation from 'modules/common/components/TransactionInformation';
import ConfirmAlert from 'modules/common/components/ConfirmAlert';
import ViewTabImage from 'modules/common/components/ViewTabImage';
import * as action from '../action';
import * as c from '../constant';
import VerifyReceipt from '../components/VerifyReceipt';
import ViewSOA from '../components/ViewSOA';
import ViewOR from '../components/ViewOR';
import ViewTaxClearance from '../components/ViewTaxClearance';

function TaxClearanceContent({
  transactionInfo,
  showTransaction,
  clearTransaction,
  isLoading,
  match,
  taxpayer,
  startAssessment,
  approveTransaction,
  rejectTransaction,
  receiveTransaction,
  permissions,
  reassess
}) {
  const setModal = useContext(ModalContext);
  const selectedTransaction = match.params.id;

  const updateData = () => {
    showTransaction(selectedTransaction);
  };

  useEffect(() => {
    if (!_.isEmpty(selectedTransaction)) {
      updateData();
    }
    return () => {
      clearTransaction();
    };
  }, []);

  useEffect(() => {
    if (!_.isEmpty(selectedTransaction)) {
      updateData();
    }
  }, [selectedTransaction]);

  const getInitials = cString => {
    if (cString) {
      const val = cString.replace(/\[^a-zA-Z- ]/g, '').match(/\b(\w)/g);
      const strFirstTwo = val.slice(0, 2);
      const final = strFirstTwo.join('');
      return final.toUpperCase();
    }
    return null;
  };

  const showVerifyReceipt = status => {
    return ['WAITING FOR PAYMENT', 'PAID', 'FOR PICK-UP', 'PENDING', 'EXPIRED REFERENCE NUMBER',].includes(
      status
    );
  };

  const handleVerifyReceipt = e => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: (
        <VerifyReceipt
          transactionInfo={transactionInfo}
          id={selectedTransaction}
        />
      ),
      title: `Assess Payment Receipt`,
      modalSize: 'modal-md',
    });
  };

  const onStartAssessment = () => e => {
    e.preventDefault();
    ConfirmAlert(
      `Are you sure, do you want start assessment?`,
      () => {
        startAssessment(selectedTransaction, () => {
          showTransaction(selectedTransaction);
        });
      },
      () => { },
      {
        yesLabel: 'Confirm',
        noLabel: 'Cancel',
      }
    );
  };

  const onApprove = () => e => {
    e.preventDefault();
    ConfirmAlert(
      'Are you sure, you want to Approve this application?',
      () => {
        approveTransaction(selectedTransaction, {}, () => {
          showTransaction(selectedTransaction);
        });
      },
      () => { },
      {
        yesLabel: 'Confirm',
        noLabel: 'Cancel',
      }
    );
  };

  const onReject = () => e => {
    e.preventDefault();

    ConfirmAlert(
      'Are you sure, you want to Reject this application?',
      remarks => {
        rejectTransaction(
          selectedTransaction,
          { remarks, module_type: 'RPTAXC' },
          () => {
            showTransaction(selectedTransaction);
          }
        );
      },
      () => { },
      {
        yesLabel: 'Confirm',
        noLabel: 'Cancel',
      },
      true
    );
  };

  const handleReceive = () => e => {
    e.preventDefault();

    ConfirmAlert(
      'Are you sure you want to tag as received this transaction?',
      remarks => {
        receiveTransaction(
          selectedTransaction,
          { remarks, module_type: 'RPTAXC' },
          () => {
            showTransaction(selectedTransaction);
          }
        );
      },
      () => { },
      {
        yesLabel: 'Confirm',
        noLabel: 'Cancel',
      },
      true
    );
  };
  
  const onReassess = e => {
    e.preventDefault();

    ConfirmAlert(
      'Are you sure you want to re assess this transaction?',
      remarks => {
        reassess(selectedTransaction, 
          { remarks },
          () => {
            showTransaction(selectedTransaction);
          }
        );
      }, 
      () => {},
      {
        yesLabel: 'Confirm',
        noLabel: 'Cancel'
      },
      true
    )
  }

  const handleViewSOA = e => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: <ViewSOA data={transactionInfo} id={selectedTransaction} />,
      title: `Statement of Account`,
      modalSize: 'modal-xl',
    });
  };

  const handleViewOR = e => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: <ViewOR data={transactionInfo} id={selectedTransaction} />,
      title: `Original Receipt`,
      modalSize: 'modal-xl',
    });
  };

  const handleViewTaxClearance = e => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: (
        <ViewTaxClearance data={transactionInfo} id={selectedTransaction} />
      ),
      title: `Tax Clearance`,
      modalSize: 'modal-xl',
    });
  };

  return (
    <>
      <div className="card-section-content container-fluid">
        {isLoading && (
          <div className="loading">
            <div className="spinner text-center">
              <span className="fas fa-1x fa-spin fa-spinner" />
            </div>
          </div>
        )}
        <div className="right-section-content">
          {/* GRAY HIGHLIGHT */}
          <div className="row highlight d-flex align-items-center mx-3 py-3">
            <div className="col-12 px-0 mt-0 mb-1">
              {/* BORDER */}
              <div className="card border-primary">
                <div className="container-fluid py-1">
                  <div className="p-0 d-flex flex-nowrap mb-3 w-100">
                    <div className="d-flex round-image justify-content-between">
                      <h3 className="d-flex align-items-center initials mb-0">
                        {_.get(
                          transactionInfo,
                          'included.taxPayer.attributes.photo'
                        ) !== '-/format/jpeg/'
                          ? (
                            <img
                              alt="Client Profile"
                              src={_.get(
                                transactionInfo,
                                'included.taxPayer.attributes.photo'
                              )}
                            />
                          ) : (
                            getInitials(
                              `${_.get(transactionInfo, 'attributes.name')}`
                            )
                          )}
                      </h3>
                    </div>
                    <div className="d-flex ml-4 align-items-center">
                      <h1 className="transaction-company m-0">
                        {`${_.get(transactionInfo, 'attributes.name') || '-'}`}
                      </h1>
                    </div>
                  </div>
                  <div className="d-flex justify-content-end mb-3">
                    {showVerifyReceipt(
                      _.get(transactionInfo, 'attributes.transaction_status')
                    )
                      && (
                        <button
                          onClick={handleVerifyReceipt}
                          className="btn btn-primary cms-bg-color-primary cms-outline-primary  font-weight-bold mr-1"
                          type="button"
                        >
                          Assess Payment Receipt
                        </button>
                      )}

                    {['FOR CHECKING'].indexOf(
                      _.get(transactionInfo, 'attributes.transaction_status')
                    ) > -1 &&
                      +_.get(
                        transactionInfo,
                        'attributes.is_start_assessment'
                      ) === 0 &&
                      _.get(
                        transactionInfo,
                        'included.module.attributes.status'
                      ) === 'PRINTED' && Acl(permissions, ['START ASSESSMENT RPTAX CLEARANCE']) && (
                        <button
                          type="button"
                          onClick={onStartAssessment(transactionInfo)}
                          className="btn btn-primary cms-bg-color-primary cms-outline-primary  gen-btn font-weight-bold mr-1"
                        >
                          Start Assessment&nbsp;
                          <i className="fas fa-check" />
                        </button>
                      )}

                    {_.get(
                      transactionInfo,
                      'attributes.is_start_assessment'
                    ) === 1
                      && Acl(permissions, ['APPROVE RPTAX CLEARANCE']) && (
                        <button
                          type="button"
                          onClick={onApprove(transactionInfo)}
                          className="gen-btn btn btn-primary cms-bg-color-primary cms-outline-primary  font-weight-bold mr-1"
                        >
                          {isLoading ? (
                            <i className="fa fa-spin fa-spinner" />
                          ) :
                            (
                              <>
                                Approve&nbsp;
                                <i className="fas fa-check" />
                              </>
                            )}
                        </button>
                      )}

                    {_.get(
                      transactionInfo,
                      'attributes.is_start_assessment'
                    ) === 1
                      && Acl(permissions, ['REJECT RPTAX CLEARANCE']) && (
                        <button
                          type="button"
                          onClick={onReject(transactionInfo)}
                          className="gen-btn btn btn-primary cms-bg-color-primary cms-outline-primary  font-weight-bold mr-1"
                        >
                          {isLoading ? (
                            <i className="fa fa-spin fa-spinner" />
                          ) :
                            (
                              <>
                                Reject&nbsp;
                                <i className="fas fa-times" />
                              </>
                            )}
                        </button>
                      )}

                    {['FOR DELIVERY'].indexOf(
                      _.get(transactionInfo, 'attributes.transaction_status')
                    ) > -1
                      && Acl(permissions, ['TAG AS RECEIVED RPTAX CLEARANCE']) && (
                        <button
                          type="button"
                          onClick={handleReceive(transactionInfo)}
                          className="btn gen-btn btn-primary cms-bg-color-primary cms-outline-primary  font-weight-bold mr-1"
                        >
                          Tag as Received&nbsp;
                        </button>
                      )}
                    {[
                      'PENDING',
                      'WAITING FOR PAYMENT',
                      'EXPIRED REFERENCE NUMBER',
                    ].indexOf(
                      _.get(transactionInfo, 'attributes.transaction_status')
                    ) > -1 &&
                      _.get(transactionInfo, 'attributes.soa_generated_at') && Acl(permissions, ['RPTAX CLEARANCE VIEW SOA']) && (
                        <button
                          onClick={handleViewSOA}
                          className="btn gen-btn btn-primary cms-bg-color-primary font-weight-bold mr-1"
                          type="button"
                        >
                          View SOA
                        </button>
                      )}

                    {_.get(transactionInfo, 'attributes.paid_at') &&
                      _.get(
                        transactionInfo,
                        'included.module.attributes.or_generated_at'
                      ) && Acl(permissions, ['RPTAX CLEARANCE VIEW OR']) && (
                        <button
                          onClick={handleViewOR}
                          className="btn gen-btn btn-primary cms-bg-color-primary font-weight-bold mr-1"
                          type="button"
                        >
                          View OR
                        </button>
                      )}

                    {_.get(transactionInfo, 'attributes.paid_at') &&
                      _.get(
                        transactionInfo,
                        'included.module.attributes.or_generated_at'
                      ) && Acl(permissions, ['RPTAX CLEARANCE VIEW E-CLEARANCE']) && (
                        <button
                          onClick={handleViewTaxClearance}
                          className="btn gen-btn btn-primary cms-bg-color-primary font-weight-bold mr-1"
                          type="button"
                        >
                          View Tax Clearance
                        </button>
                      )}
                    {['PENDING'].indexOf(_.get(transactionInfo, 'attributes.transaction_status')) > -1 &&
                      Acl(permissions, ['REASSESS RPTAX CLEARANCE']) && (
                        <button 
                          onClick={onReassess}
                          className='btn gen-btn btn-primary cms-bg-color-primary font-weight-bold mr-1'
                          type='button'
                        >
                          Re Assess
                        </button>
                      )}
                  </div>

                  <Tabs>
                    <TabList>
                      <Tab>Transaction</Tab>
                      <Tab>Account Details</Tab>
                      <Tab>Requirements</Tab>
                    </TabList>
                    <TabPanel>
                      <TransactionInformation
                        module="rpts"
                        transactionInfo={transactionInfo}
                        taxpayer={taxpayer}
                      />
                    </TabPanel>
                    <TabPanel>
                      <div className="col-12 p-3">
                        <table className="transaction-table table table-borderless ">
                          <tbody>
                            <tr>
                              <td className="text-dark" width="250">
                                Account Name
                              </td>
                              <td className="text-dark" width="1">
                                :
                              </td>
                              <td>
                                <b>
                                  {(
                                    _.get(transactionInfo, 'attributes.name') ||
                                    '--'
                                  ).toUpperCase()}
                                </b>
                              </td>
                            </tr>
                            <tr>
                              <td className="text-dark" width="250">
                                TDN
                              </td>
                              <td className="text-dark" width="1">
                                :
                              </td>
                              <td>
                                <b>
                                  {(
                                    _.get(
                                      transactionInfo,
                                      'included.module.attributes.tdn'
                                    ) || '--'
                                  ).toUpperCase()}
                                </b>
                              </td>
                            </tr>
                            <tr>
                              <td className="text-dark" width="250">
                                PIN
                              </td>
                              <td className="text-dark" width="1">
                                :
                              </td>
                              <td>
                                <b>
                                  {(
                                    _.get(
                                      transactionInfo,
                                      'included.module.attributes.pin'
                                    ) || '--'
                                  ).toUpperCase()}
                                </b>
                              </td>
                            </tr>
                            <tr>
                              <td className="text-dark" width="250">
                                Number of Copies
                              </td>
                              <td className="text-dark" width="1">
                                :
                              </td>
                              <td>
                                <b>
                                  {_.get(
                                    transactionInfo,
                                    'included.module.attributes.number_of_copies'
                                  )}
                                </b>
                              </td>
                            </tr>
                            <tr>
                              <td className="text-dark" width="250">
                                Owner or Representative
                              </td>
                              <td className="text-dark" width="1">
                                :
                              </td>
                              <td>
                                <b>
                                  {_.get(
                                    transactionInfo,
                                    'included.module.attributes.is_representative'
                                  ) === 0
                                    ? 'OWNER'
                                    : 'REPRESENTATIVE'}
                                </b>
                              </td>
                            </tr>
                            <tr>
                              <td className="text-dark" width="250">
                                Purpose
                              </td>
                              <td className="text-dark" width="1">
                                :
                              </td>
                              <td>
                                <b>
                                  {_.get(
                                    transactionInfo,
                                    'included.module.attributes.purpose'
                                  )}
                                </b>
                              </td>
                            </tr>
                            <tr>
                              <td className="text-dark" width="250">
                                Authorization Leter (if Representative)
                              </td>
                              <td className="text-dark" width="1">
                                :
                              </td>
                              <td>
                                {_.get(
                                  transactionInfo,
                                  'included.module.attributes.is_representative'
                                ) === 1
                                  ? (
                                    <img
                                      src={
                                        _.get(
                                          transactionInfo,
                                          'included.module.attributes.authorization_letter'
                                        ) || ''
                                      }
                                      alt=""
                                      className="img-fluid"
                                      width="100%"
                                    />
                                  ) : (
                                    '--'
                                  )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </TabPanel>
                    <TabPanel>
                      <div className="p-3">
                        <ViewTabImage
                          label="Requirements"
                          data={(
                            _.get(
                              transactionInfo,
                              'included.requirementFiles'
                            ) || []
                          ).map(x => ({
                            ...x,
                            id: x.id,
                            name: x.attributes._application_requirement,
                            content: x.attributes.url,
                          }))}
                        />
                      </div>
                    </TabPanel>
                  </Tabs>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

TaxClearanceContent.propTypes = {
  // selectedTransaction: PropTypes.string.isRequired,
  match: PropTypes.instanceOf(Object).isRequired,
  taxpayer: PropTypes.instanceOf(Object).isRequired,
  showTransaction: PropTypes.instanceOf(Function).isRequired,
  transactionInfo: PropTypes.instanceOf(Object).isRequired,
  clearTransaction: PropTypes.instanceOf(Function).isRequired,
  startAssessment: PropTypes.instanceOf(Function).isRequired,
  approveTransaction: PropTypes.instanceOf(Function).isRequired,
  rejectTransaction: PropTypes.instanceOf(Function).isRequired,
  receiveTransaction: PropTypes.instanceOf(Function).isRequired,
  permissions: PropTypes.instanceOf(Object).isRequired,
  isLoading: PropTypes.bool.isRequired,
  reassess: PropTypes.instanceOf(Function).isRequired
};

const mapStateToProps = ({ api }) => ({
  transactionInfo: _.get(api, `${c.SHOW_TRANSACTION}.item`) || {},
  taxpayer:
    {
      ...(_.get(api, `${c.SHOW_TRANSACTION}.item.included.module.attributes`) ||
        {}),
    } || {},
  isLoading: api.loading.indexOf(c.SHOW_TRANSACTION) > -1,
  permissions: getJson('permissions'),
});

const enhance = _.flowRight([withRouter, connect(mapStateToProps, action)]);

export default enhance(TaxClearanceContent);
